// SweetAlert prompts for managing My Scenarios folders. `component` is the calling
// Vue component, used for $swal, $store and $router.

const STORE = "scenarioDirectoriesStore";

function bootstrapSwal(component) {
  return component.$swal.mixin({
    customClass: {
      confirmButton: "btn btn-primary mx-1",
      cancelButton: "btn btn-light mx-1",
    },
    buttonsStyling: false,
  });
}

// Brief confirmation in the corner that disappears by itself
export function showToast(component, text) {
  return component.$swal
    .mixin({
      toast: true,
      position: "top-end",
      timer: 3000,
      showConfirmButton: false,
    })
    .fire({ type: "success", titleText: text });
}

// Name of a move destination; null is the root
export function folderName(component, uuid) {
  if (!uuid) return "My Scenarios";

  const folder = component.$store.getters[`${STORE}/flatDirectories`].find(
    (d) => d.uuid === uuid
  );
  return folder ? folder.name : "the folder";
}

function promptForName(
  component,
  { title, confirmButtonText, inputValue, save, successMessage }
) {
  let savedName;

  return bootstrapSwal(component)
    .fire({
      title,
      input: "text",
      inputValue: inputValue || "",
      inputAttributes: { maxlength: 255, "aria-label": "Folder name" },
      showCancelButton: true,
      confirmButtonText,
      showLoaderOnConfirm: true,
      inputValidator: (value) =>
        !value || !value.trim() ? "Please enter a folder name" : null,
      preConfirm: (value) => {
        savedName = value.trim();
        return save(savedName).catch((error) => {
          component.$swal.showValidationMessage(
            typeof error === "string" ? error : "Couldn't save folder"
          );
          return false;
        });
      },
    })
    .then((result) => {
      if (result.value) showToast(component, successMessage(savedName));
    });
}

export function createFolder(component, parentUuid = null) {
  return promptForName(component, {
    title: parentUuid ? "New subfolder" : "New folder",
    confirmButtonText: "Create",
    successMessage: (name) => `Created "${name}"`,
    save: (name) =>
      component.$store.dispatch(`${STORE}/createDirectory`, {
        name,
        parentUuid,
      }),
  });
}

export function renameFolder(component, directory) {
  return promptForName(component, {
    title: "Rename folder",
    confirmButtonText: "Rename",
    inputValue: directory.name,
    successMessage: (name) => `Renamed to "${name}"`,
    save: (name) =>
      component.$store.dispatch(`${STORE}/renameDirectory`, {
        uuid: directory.uuid,
        name,
      }),
  });
}

export function deleteFolder(component, directory, parentUuid = null) {
  const parent = component.$store.getters[`${STORE}/flatDirectories`].find(
    (d) => d.uuid === parentUuid
  );
  const parentName = parentUuid
    ? (parent && parent.name) || "the parent folder"
    : "My Scenarios";

  const div = document.createElement("div");
  div.textContent = parentName;
  const safeParentName = div.innerHTML;

  return bootstrapSwal(component)
    .fire({
      // titleText, not title, as title is rendered as HTML
      titleText: `Delete "${directory.name}"?`,
      html: `Any scenarios and subfolders in this folder will be moved to <b>${safeParentName}</b>. No scenarios will be deleted.`,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete folder",
      showLoaderOnConfirm: true,
      preConfirm: () =>
        component.$store
          .dispatch(`${STORE}/deleteDirectory`, directory.uuid)
          .catch((error) => {
            component.$swal.showValidationMessage(
              typeof error === "string" ? error : "Couldn't delete folder"
            );
            return false;
          }),
    })
    .then((result) => {
      if (!result.value) return;

      showToast(component, `Deleted "${directory.name}"`);

      // Don't leave the user looking at a folder that no longer exists
      const route = component.$route;
      if (route.params.directoryUuid === directory.uuid) {
        component.$router.push(
          parentUuid ? `/me/scenarios/${parentUuid}` : "/me/scenarios"
        );
      }
    });
}

export function moveToFolder(component, target) {
  component.$store.commit(`${STORE}/setMoveTarget`, target);
}
